import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">A1C Survival Plan</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-slate prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Privacy Policy</h1>
          <p className="text-xl text-teal-600 font-medium mb-2">A1C Survival Plan</p>
          <p className="text-slate-500 mb-8">Effective Date: December 01, 2025</p>

          <p className="text-slate-600 leading-relaxed">
            A1C Survival Plan ("we," "our," or "the app") is committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, and safeguard your information when 
            you use our app and related services.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">1. Information We Collect</h2>
          <p className="text-slate-600 leading-relaxed">
            We collect information you choose to provide, including:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li>Journal entries</li>
            <li>Health-related inputs (such as glucose notes, nutrition logs, activity logs, hydration tracking, and wellness reflections)</li>
            <li>App usage data (to improve functionality and performance)</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mt-4">
            We do not require you to provide medical records or connect external services to use the app.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">2. How We Use Your Information</h2>
          <p className="text-slate-600 leading-relaxed">
            Your information is used to:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li>Help you track, organize, and reflect on your diabetes management</li>
            <li>Generate insights and patterns within the app (A1C SP Signals)</li>
            <li>Improve app performance, usability, and features</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mt-4 font-medium">
            Your data is never used for advertising purposes.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">3. Data Sharing</h2>
          <p className="text-slate-600 leading-relaxed font-medium">
            We do not sell, rent, or trade your personal or health-related data.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            Your information may only be shared:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li>With trusted service providers necessary to operate the app (e.g., secure hosting or analytics)</li>
            <li>When required by law</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mt-4">
            All service providers are required to follow strict confidentiality and security standards.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">4. Data Storage & Security</h2>
          <p className="text-slate-600 leading-relaxed">
            We use reasonable administrative, technical, and physical safeguards to protect your data.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            While no system is 100% secure, we design A1C Survival Plan with privacy and data protection as a core principle.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">5. Your Choices & Control</h2>
          <p className="text-slate-600 leading-relaxed font-medium">
            You control your data.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            You may:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li>Edit or delete journal entries</li>
            <li>Request deletion of your account and associated data</li>
            <li>Choose which information you log or share</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mt-4">
            Instructions for data deletion are available within the app or by contacting us directly.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">6. Third-Party Integrations</h2>
          <p className="text-slate-600 leading-relaxed">
            If you choose to connect third-party services (such as Apple Health), data shared is governed by those services' privacy policies.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            We only access data you explicitly allow.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">7. Children's Privacy</h2>
          <p className="text-slate-600 leading-relaxed">
            A1C Survival Plan is not intended for children under 13. We do not knowingly collect data from children.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">8. Changes to This Policy</h2>
          <p className="text-slate-600 leading-relaxed">
            We may update this Privacy Policy as the app evolves. Updates will be posted within the app and on our website.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">9. Contact Us</h2>
          <p className="text-slate-600 leading-relaxed">
            If you have questions or requests regarding privacy, contact:
          </p>
          <div className="bg-slate-50 rounded-2xl p-6 mt-4">
            <p className="text-slate-600">
              <span className="font-medium">Email:</span>{' '}
              <a href="mailto:support@a1csurvivalplan.com" className="text-teal-600 hover:text-teal-700">
                support@a1csurvivalplan.com
              </a>
            </p>
            <p className="text-slate-600 mt-2">
              <span className="font-medium">App Name:</span> A1C Survival Plan
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-100 mt-16">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} A1C Survival Plan. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
